import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { ConfiguredApolloProvider } from "./components/graphql/ConfiguredApolloProvider.tsx";
import { Layout } from "./components/HomeLayout";
import { KetchConsentTag } from "./components/KetchConsentTag";
import { FeatureFlagRoute } from "./components/ProtectedRoutes";
import { OneClickLoginCodeProvider } from "./context/OneClickLoginCodeContext.tsx";
import { AuthProvider } from "./context/AuthContext";
import Home from "./pages/Home";
import LandingPage from "./pages/LandingPage";

import "./styles/animations.css";
import { lazy, Suspense } from "react";

const ConditionLandingPage = lazy(
  () => import("./pages/ConditionLandingPage"),
);

const BPUploadPage = lazy(() => import("./pages/BPUploadPage"));

function App() {
  // Internal testing tools - only available in development
  let devRoutes = null;
  if (import.meta.env.DEV) {
    const DynamicAttributeTester = lazy(() =>
      import("./pages/DynamicAttributeTester").then((m) => ({
        default: m.DynamicAttributeTester,
      })),
    );
    const AIMedicationTester = lazy(() =>
      import("./pages/AIMedicationTesterV2").then((m) => ({
        default: m.AIMedicationTester,
      })),
    );
    devRoutes = (
      <>
        <Route
          path="/internal/dynamic-tester"
          element={
            <Suspense fallback={null}>
              <DynamicAttributeTester />
            </Suspense>
          }
        />
        <Route
          path="/internal/ai-medication-tester"
          element={
            <Suspense fallback={null}>
              <AIMedicationTester />
            </Suspense>
          }
        />
      </>
    );
  }

  return (
    <HelmetProvider>
      <ConfiguredApolloProvider>
        <Router>
          <OneClickLoginCodeProvider>
            <AuthProvider>
              <KetchConsentTag />
              <Routes>
                {devRoutes}
                {/* Blood-pressure upload deep link (must precede /:id/* catch-all) */}
                <Route
                  path="/bp-upload/:token"
                  element={
                    <Suspense fallback={null}>
                      <BPUploadPage />
                    </Suspense>
                  }
                />
                {/* Feature Flag enabled routes */}
                <Route element={<FeatureFlagRoute />}>
                  {/* Condition landing pages (/cond/:conditionId) */}
                  <Route
                    path="/cond/:conditionId"
                    element={
                      <Suspense fallback={null}>
                        <ConditionLandingPage />
                      </Suspense>
                    }
                  />
                  {/* Partner path-prefix route (/:partnerSlug/:landingId).
                      Ranked above /:id/* by react-router; both render LandingPage. */}
                  <Route
                    path="/:partnerSlug/:landingId/*"
                    element={<LandingPage />}
                  />
                  {/* Landing page route with ID parameter */}
                  <Route path="/:id/*" element={<LandingPage />} />
                </Route>
                {/* Home page route */}
                <Route
                  path="/"
                  element={
                    <Layout>
                      <Home />
                    </Layout>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace={true} />} />
              </Routes>
            </AuthProvider>
          </OneClickLoginCodeProvider>
        </Router>
      </ConfiguredApolloProvider>
    </HelmetProvider>
  );
}

export default App;
